/**
 * The raid screen's widgets: scene, gauges, chips, found grid, event feed and
 * the four focus plates. Markup lives in ../raid-panel.ts.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it.
 */
export default `
/* ── Chips: the label/value unit used wherever a number needs a home ───── */
/* Generated icon art inside a chip, bar label or badge. */
.bv-ico { width: 16px; height: 16px; flex: none; object-fit: contain; display: inline-block; vertical-align: middle; filter: drop-shadow(0 1px 1px rgba(0,0,0,0.8)); }
.bv-chip-row { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }
.bv-chip {
  position: relative; display: inline-flex; align-items: center; gap: 6px; padding: 3px 9px;
  border: 1px solid var(--line); border-radius: 999px;
  background:
    radial-gradient(circle at 18% 30%, rgba(255,255,255,0.05) 0 1px, transparent 1.5px) 0 0 / 9px 7px,
    linear-gradient(180deg, #1c150d, var(--well));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.12), inset 0 -1px 2px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5);
  font-family: var(--font-technical); font-variant-numeric: tabular-nums;
}
.bv-chip small { color: var(--bone-dim); font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase; }
.bv-chip b { display: inline-flex; align-items: center; gap: 3px; color: var(--bone); font-size: 0.85rem; font-weight: 400; }
.bv-chip-good b { color: var(--moss); }
.bv-chip-warn b { color: var(--warn); }
.bv-chip-bad b { color: var(--rust); }
.bv-chip-good { border-color: var(--moss-dark); }
.bv-chip-warn { border-color: var(--warn); }
.bv-chip-bad { border-color: var(--rust); }


/* ── The scene: one looping clip, badges laid over it ─────────────────── */
.bv-raid { display: flex; flex-direction: column; gap: 8px; width: 100%; }
.bv-raid-scene {
  position: relative; width: 100%; height: 92px; overflow: hidden;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: var(--well); box-shadow: inset 0 2px 6px rgba(0,0,0,0.7);
}
.bv-raid-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: 50% 60%; display: block; }
.bv-raid-scene::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, rgba(0,0,0,0.45) 0%, transparent 32%, transparent 68%, rgba(0,0,0,0.5) 100%);
}
.bv-raid-badge {
  position: absolute; z-index: 2; display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px; border: 1px solid var(--line); border-radius: var(--radius-well);
  background: rgba(12,10,7,0.78); color: var(--bone);
  font: 700 0.68rem var(--font-display); letter-spacing: 0.12em; text-transform: uppercase;
}
.bv-raid-badge-focus { left: 6px; top: 6px; color: var(--brass-hot); border-color: var(--brass); }
.bv-raid-badge-hours { right: 6px; top: 6px; }
.bv-raid-badge-hours .bv-ico { width: 18px; height: 18px; }
.bv-raid-badge-hours b { font: 700 0.95rem var(--font-technical); font-variant-numeric: tabular-nums; }
.bv-raid-badge-hours small { color: var(--bone-dim); font: 0.6rem var(--font-technical); letter-spacing: 0.08em; }

/* ── Bars: the HUD's own classes do the drawing (hud.css.ts); this only
   spaces them inside the raid strip and sizes the label gutter. ─────────── */
.bv-raid-row { margin-bottom: 0; }
.bv-raid-body .bv-hud-label { width: 38px; display: inline-flex; align-items: center; }
.bv-raid-energy .bv-hud-label { width: 24px; }
.bv-raid-energy .bv-ico { width: 20px; height: 20px; }
/* The raid's cost, shaded onto the end of the energy actually held. */
.bv-raid-cost-shade {
  position: absolute; top: 0; bottom: 0; z-index: 1; pointer-events: none;
  background: repeating-linear-gradient(135deg, rgba(168,69,44,0.75) 0 4px, rgba(90,28,16,0.75) 4px 8px);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.4);
}

/* ── Zone titles: the zone bold, underlined, in its map colour ────────── */
.bv-raid-title {
  margin: 0 0 6px; color: var(--bone);
  font: 700 1.05rem var(--font-display); letter-spacing: 0.04em;
}
.bv-raid-title u { font-weight: 800; text-decoration-thickness: 2px; text-underline-offset: 3px; text-transform: uppercase; }
.bv-tier-1 { color: var(--moss); }
.bv-tier-2 { color: var(--warn); }
.bv-tier-3 { color: var(--rust); }
.bv-raid-title u.bv-tier-1, .bv-raid-title u.bv-tier-2, .bv-raid-title u.bv-tier-3 { text-decoration-color: currentColor; }
.bv-tier-chip { border-color: currentColor; }
.bv-tier-chip small, .bv-tier-chip b { color: inherit; }

/* ── Found: what the run has turned up so far ─────────────────────────── */
.bv-found { display: flex; flex-direction: column; gap: 6px; }
.bv-found-head { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.bv-found-title { color: var(--brass-hot); font: 700 0.75rem var(--font-display); letter-spacing: 0.12em; text-transform: uppercase; }
.bv-found-count { color: var(--bone); font: 0.75rem var(--font-technical); font-variant-numeric: tabular-nums; font-weight: 400; }
.bv-found-full {
  margin-left: auto; padding: 1px 7px; border: 1px solid var(--rust); border-radius: var(--radius-well);
  background: rgba(168,69,44,0.18); color: var(--rust); font: 700 0.62rem var(--font-display); letter-spacing: 0.08em; text-transform: uppercase;
}
.bv-found-full[hidden], .bv-found-pick[hidden], .bv-win-err[hidden] { display: none; }
/* A SCROLL BOX (overflow-y), so a slot's hover lift -- up 3px and 5% bigger,
   item.css.ts -- was sheared off at the grid's edge: the top row slid "under"
   the header the moment it was hovered. The padding is headroom for the lift
   and the negative margin pulls the grid back so nothing moves at rest. Same
   fix as .bv-shop-grid in commerce.css.ts. */
.bv-found-grid {
  display: grid; grid-template-columns: repeat(var(--slot-cols, 5), minmax(0, 1fr)); gap: 4px;
  max-height: 202px; overflow-y: auto; scrollbar-width: thin;
  padding: 8px 5px 4px; margin: -8px -5px -4px;
}
.bv-found-grid .bv-slot-filled:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 1px; }
.bv-found-grid .bv-slot[data-junk="true"] .bv-slot-art { opacity: 0.6; }
.bv-found-grid .bv-slot-picked { border-color: var(--brass-hot); box-shadow: inset 0 0 12px rgba(255,189,92,0.3), 0 0 8px rgba(255,189,92,0.25); }
/* Picked item: the icon on the left, a touch larger than the block so it
   overflows the top, bottom and left edges a little and fills the plate
   (sized past the edge by more than looks needed: icon files carry ~10%
   transparent padding);
   the name across the top and the weight and Drop under it. */
.bv-found-pick {
  display: grid; grid-template-columns: 64px minmax(0, 1fr) auto; grid-template-areas: "art name name" "art chip drop";
  align-items: center; gap: 4px 6px; padding: 6px 8px 6px 4px; margin-left: 6px; overflow: visible;
  border: 1px solid var(--brass); border-radius: var(--radius-well); background: rgba(0,0,0,0.35);
}
.bv-found-pick-art {
  grid-area: art; align-self: center; width: 96px; height: 96px; margin: -12px 0 -12px -22px;
  display: flex; align-items: center; justify-content: center; pointer-events: none;
}
.bv-found-pick-art img { max-width: 100%; max-height: 100%; object-fit: contain; filter: drop-shadow(0 3px 4px rgba(0,0,0,0.7)); }
.bv-found-pick-name { grid-area: name; min-width: 0; color: var(--bone); font: 700 0.8rem var(--font-display); letter-spacing: 0.05em; }
.bv-found-pick .bv-chip { grid-area: chip; justify-self: start; white-space: nowrap; }
.bv-found-drop { grid-area: drop; min-height: 36px; }

/* ── Extraction choice (rules 2): the haul, two gauges, Carry home ────── */
/* The strip and the picker swap with the hidden attribute; .bv-act sets a
   display of its own, which would otherwise keep a hidden button on screen. */
.bv-raid [hidden] { display: none !important; }
.bv-extract { display: flex; flex-direction: column; gap: 8px; }
.bv-extract-note { margin: -4px 0 2px; color: var(--bone-dim); font: 0.75rem var(--font-technical); }
.bv-extract-grid .bv-extract-left .bv-slot-art { opacity: 0.38; filter: grayscale(0.7); }
.bv-extract-grid .bv-extract-sel { outline: 2px solid var(--brass-hot); outline-offset: 1px; }
.bv-extract-part {
  position: absolute; left: 3px; bottom: 3px; padding: 0 4px; border-radius: 3px;
  background: rgba(0,0,0,0.75); color: var(--brass-hot); font: 700 0.6rem var(--font-technical);
}
.bv-extract-qty { align-items: center; }
.bv-extract-step { min-width: 44px; min-height: 44px; padding: 0; font-size: 1.2rem; }
.bv-extract-n { min-width: 2.5ch; text-align: center; color: var(--bone); font: 700 1rem var(--font-technical); font-variant-numeric: tabular-nums; }
.bv-extract-acts { display: flex; flex-wrap: wrap; gap: 6px; }
.bv-extract-acts .bv-act { min-height: 44px; }
.bv-extract-acts [data-x="go"] { flex: 1 1 100%; }

/* ── The event feed: a few recent lines, each with a status pip ───────── */
.bv-feed {
  list-style: none; margin: 10px 0 0; padding: 0 2px 0 0; display: flex; flex-direction: column; gap: 3px;
  /* Four rows show; the rest scroll, so a long raid cannot grow the panel. */
  max-height: 108px; overflow-y: auto; scrollbar-width: thin;
}
.bv-feed-row { flex: none; }
.bv-feed-row {
  display: flex; align-items: center; gap: 8px; padding: 3px 8px;
  border: 1px solid var(--line); border-radius: var(--radius-well); background: rgba(0,0,0,0.28);
  color: var(--bone-dim); font: 0.75rem var(--font-technical);
}
.bv-feed-row i { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--bone-dim); }
.bv-feed-good i { background: var(--moss); }
.bv-feed-warn i { background: var(--warn); }
.bv-feed-bad i { background: var(--rust); }
.bv-feed-row:first-child { color: var(--bone); border-color: #4a3920; }

/* ── Focus plates: generated art behind, name over it, effects under ──── */
.bv-focus {
  display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-end; gap: 5px;
  min-height: 74px; padding: 8px 10px; text-align: left; letter-spacing: normal; text-transform: none;
  background-size: cover; background-position: center; color: var(--bone);
}
.bv-focus[data-focus="FOOD"] { background-image: linear-gradient(180deg, rgba(8,6,4,0.35), rgba(8,6,4,0.82)), url("/ui/raid/focus-food.jpg"); }
.bv-focus[data-focus="SCRAP"] { background-image: linear-gradient(180deg, rgba(8,6,4,0.35), rgba(8,6,4,0.82)), url("/ui/raid/focus-scrap.jpg"); }
.bv-focus[data-focus="BOUNTY"] { background-image: linear-gradient(180deg, rgba(8,6,4,0.35), rgba(8,6,4,0.82)), url("/ui/raid/focus-bounty.jpg"); }
.bv-focus[data-focus="EXPLORE"] { background-image: linear-gradient(180deg, rgba(8,6,4,0.35), rgba(8,6,4,0.82)), url("/ui/raid/focus-explore.jpg"); }
.bv-focus-name { font: 700 1rem var(--font-display); letter-spacing: 0.12em; text-transform: uppercase; text-shadow: 0 1px 0 #000, 0 0 8px rgba(0,0,0,0.8); }
.bv-focus-fx { display: flex; flex-wrap: wrap; gap: 3px; }
.bv-fx {
  padding: 1px 5px; border-radius: 3px; background: rgba(0,0,0,0.62);
  font: 700 0.6rem var(--font-technical); letter-spacing: 0.03em; white-space: nowrap;
}
.bv-fx-up { color: var(--moss); }
.bv-fx-down { color: var(--rust); }
.bv-fx-risk { color: var(--warn); }
.bv-wm-side .bv-focus { min-height: 74px; }
@media (max-width: 720px) {
  .bv-wm-side .bv-focus-row .bv-focus, .bv-wm-side .bv-wm-focus .bv-focus { flex: 1 1 44%; width: auto; }
}

/* ── Result: the backpack, the verdict and the notice ─────────────────── */
.bv-result { position: relative; }
.bv-result-pack {
  position: absolute; top: -4px; right: 0; width: 46px; height: 46px; cursor: default;
  transform-origin: 50% 90%; transition: transform 0.2s ease; filter: drop-shadow(0 3px 3px rgba(0,0,0,0.7));
}
.bv-result-pack .bv-ico { width: 100%; height: 100%; filter: none; }
.bv-result:hover .bv-result-pack, .bv-result-pack:hover { animation: bvresult-pack 0.7s ease-in-out; }
@keyframes bvresult-pack {
  0% { transform: rotate(0) translateY(0); }
  25% { transform: rotate(-9deg) translateY(-4px); }
  55% { transform: rotate(7deg) translateY(-2px); }
  100% { transform: rotate(0) translateY(0); }
}
.bv-wm-verdict { display: flex; align-items: center; gap: 8px; font-size: 1.15rem; }
.bv-wm-verdict .bv-ico { width: 26px; height: 26px; }
.bv-notice {
  margin: 6px 0 4px; padding: 7px 9px; border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    radial-gradient(circle at 12% 30%, rgba(255,255,255,0.05) 0 1px, transparent 1.6px) 0 0 / 9px 7px,
    linear-gradient(180deg, rgba(255,189,92,0.12), rgba(0,0,0,0.3));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.18), 0 2px 4px rgba(0,0,0,0.45);
}
.bv-notice-head {
  display: flex; align-items: center; gap: 6px; margin-bottom: 6px; color: var(--brass-hot);
  font: 700 0.78rem var(--font-display); letter-spacing: 0.12em; text-transform: uppercase;
}
.bv-notice-zones { display: flex; flex-wrap: wrap; gap: 5px; }
.bv-zone-chip { border-color: currentColor; font: 700 0.72rem var(--font-technical); letter-spacing: 0.04em; text-transform: uppercase; }
@media (prefers-reduced-motion: reduce) { .bv-result:hover .bv-result-pack, .bv-result-pack:hover { animation: none; } }
`;
